import { isEmptyValue, toInt } from 'aa_common/front-end/helpers'
import { AssetLedger } from 'common/models'
import { PresenterFixedAssetDepreciationResponse } from 'common/open-api'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectMonthlyClosingCurrent } from 'store/accounting/monthlyClosing/selectors'
import { selectAssetItemData } from 'store/asset/selectors'
import { useDebouncedCallback } from 'use-debounce'

import { adjustHistoryStore } from '../components/accelerated-depreciation-drawer/stores/adjust-history'
import { addExtraRowsAcceleratedDepreciation } from '../helpers/add-extra-rows-accelerated-depreciation'
import { filterEndMonthlyDepreciation, filterStartMonthlyDepreciation } from '../helpers/filter-monthly-depreciation'
import { initializeDepreciationAmount } from '../helpers/initialize-depreciation-amount'
import { selectFromFiscalYearAcceleratedDepr } from '../helpers/select-from-fiscal-year-accelerated-depr'
import { shouldShowEmptyDepreciationTable } from '../helpers/should-show-empty-depreciation-table'
import { updateDepreciationAmount } from '../helpers/update-depreciation-amount'
import { AcceleratedDepreciationItem } from '../types'
import { useGetExtraTermMonths } from './use-get-extra-term-months'
import { useMonthlyDepreciation } from './use-monthly-depreciation'

export const useCalculateAcceleratedDepreciation = (
  currentDepreciationResult: PresenterFixedAssetDepreciationResponse,
  assetLedger?: AssetLedger,
  selectedDate?: string,
  isDateChanged?: boolean
) => {
  const [acceleratedDepreciation, setAcceleratedDepreciation] = useState<AcceleratedDepreciationItem[]>([])
  const originalMonthlyDepreciation = useMonthlyDepreciation(
    currentDepreciationResult,
    assetLedger?.ledger_setting,
    isDateChanged
  )
  // - work-around to wait get extra term month before calculating depreciation
  // - The issue: the depreciation is calculated twice with different values when selecting a date out of FA service life
  const [isGettingTermMonths, setIsGettingTermMonths] = useState(true)

  const extraTermMonths = useGetExtraTermMonths(originalMonthlyDepreciation, setIsGettingTermMonths, selectedDate)
  const currentMonthlyClosing = useSelector(selectMonthlyClosingCurrent, isEqual)
  const monthlyClosingWhenRegister = adjustHistoryStore.useSlice(state => state.data?.monthly_closing)
  const assetDetail = useSelector(selectAssetItemData, isEqual)
  const startUsage = get(assetDetail, 'usage_started_at') as string
  const transitionAt = get(assetDetail, 'transition_at') as string

  useEffect(() => {
    if (isGettingTermMonths) return
    setIsGettingTermMonths(true)

    if (!selectedDate || !originalMonthlyDepreciation.length) return
    if (
      isDateChanged &&
      shouldShowEmptyDepreciationTable(
        selectedDate,
        startUsage,
        transitionAt,
        originalMonthlyDepreciation[0].start_date as string,
        currentMonthlyClosing
      )
    ) {
      return setAcceleratedDepreciation([])
    }

    const retirementDepreciationEndedMethod = assetLedger?.ledger_setting
      ?.retirement_depreciation_ended_method as number
    const memorandum = assetLedger?.memorandum_value
    const residualValue = assetLedger?.residual_amount

    if (isDateChanged) {
      const monthlyDepreciationExtraRows = addExtraRowsAcceleratedDepreciation(
        originalMonthlyDepreciation,
        extraTermMonths
      )
      const filteredStartMonthlyDepreciation = filterStartMonthlyDepreciation(
        monthlyDepreciationExtraRows,
        currentMonthlyClosing
      )
      const filteredEndMonthlyDepreciation = filterEndMonthlyDepreciation(
        filteredStartMonthlyDepreciation,
        selectedDate,
        retirementDepreciationEndedMethod
      )
      setAcceleratedDepreciation(
        initializeDepreciationAmount(filteredEndMonthlyDepreciation, memorandum, residualValue)
      )
    } else {
      const selectedFiscalYear = selectFromFiscalYearAcceleratedDepr(currentMonthlyClosing, monthlyClosingWhenRegister)
      setAcceleratedDepreciation(
        filterStartMonthlyDepreciation(originalMonthlyDepreciation, {
          from_term_month: { term: { year: selectedFiscalYear } },
        })
      )
    }
  }, [
    originalMonthlyDepreciation,
    assetLedger,
    selectedDate,
    isDateChanged,
    currentMonthlyClosing,
    monthlyClosingWhenRegister,
    transitionAt,
    startUsage,
    extraTermMonths,
    isGettingTermMonths,
    setIsGettingTermMonths,
  ])

  const handleInputChanged = useDebouncedCallback((newValue: string, key: string) => {
    const numberValue = isEmptyValue(newValue.trim()) ? '0' : newValue.replace(/[¥,]/g, '')
    const formatValue = Number.isNaN(Number(numberValue)) ? '0' : numberValue
    setAcceleratedDepreciation(updateDepreciationAmount(acceleratedDepreciation, key, toInt(formatValue)))
  }, 250)

  return {
    acceleratedDepreciation,
    handleInputChanged,
  }
}
